// take EnhancedProductGroup, EnhancedProductFamily, EnhancedProductVariant and give reasonable ground truths
// this is being done to reuse the logic to build meta data for individual facets without doing a full data aggregation
import type { EnhancedProductFamily } from '#api/enhancements/enhancedTmsApiTypes'
import { assert } from '#app/assert'
import type { ConfigurationProductFamily } from '#app/store/slices/configurationSlice'

/**
 * The selection as given by family dataset, regardless of user selection
 */
export const familyInitialSelection = <T extends EnhancedProductFamily = EnhancedProductFamily>(family: T): T['variants'][number] | undefined => {
  const result = family.variants.find((x) => x.isSelected)
  assert(result !== undefined, `Expected a family to always have a family.variant with isSelected = true, family: ${family.id}`)
  return result
}

/**
 * Get the currently selected family variant with respect of the user choice
 */
export const selectedFamilyVariant = <T extends EnhancedProductFamily = EnhancedProductFamily>(
  family: T,
  config: ConfigurationProductFamily | undefined,
): T['variants'][number] | undefined => {
  const selectedVariant = config?.selectedVariant
  return selectedVariant ? family.variants.find((x) => x.id === selectedVariant.id) : familyInitialSelection(family)
}

/**
 * Is a variant selected incorporating current user choice
 */
export const isVariantSelected = <T extends EnhancedProductFamily = EnhancedProductFamily>(
  family: T,
  variant: T['variants'][number],
  config: ConfigurationProductFamily | undefined,
) => {
  const selectedVariant = selectedFamilyVariant(family, config)
  return !!(selectedVariant ? selectedVariant.id === variant.id : familyInitialSelection(family))
}
